import type {
  BackendShippingProviderName,
  CreateOrderResponse,
  OrderDTO,
  ShippingProviderName
} from 'ecosystem'
import { type ShippingProviderOpts } from '../types'
import { type CarrierContext } from './types'
import { openIngridSession } from './ingridSession'
import { openNshiftSession } from './nshiftSession'

/**
 * What a carrier contributes to the order, once its session is open.
 *
 * `shipping` is spread onto the SF order at creation; `response` is echoed back
 * to the checkout alongside the PSP session. DEFAULT contributes neither — it
 * has no carrier session, and omitting `shipping` is what leaves
 * `Order.shippingProvider` unset.
 */
export interface CarrierSession {
  /** Which variant ran. DEFAULT is the only one without a carrier-owned
   *  delivery step, so it is the only one that prices a freight row. */
  provider: ShippingProviderName
  shipping?: {
    provider: BackendShippingProviderName
    providerSessionId?: OrderDTO['shippingId']
  }
  response?: Partial<CreateOrderResponse>
}

/**
 * Opens the carrier session for the store's configured shipping provider.
 *
 * Failures surface as a thrown error rather than a return value: the route
 * entry already funnels every throw in this flow into a single 400, and doing
 * it here keeps the pipeline free of a second error channel.
 */
export const openCarrierSession = async (
  ctx: CarrierContext,
  opts: ShippingProviderOpts
): Promise<CarrierSession> => {
  switch (opts.shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'DEFAULT':
      return { provider: 'DEFAULT' }

    case 'POSTNORD':
      return { provider: 'POSTNORD', shipping: { provider: 'POSTNORD' } }

    case 'INGRID': {
      const { error, data: ingridResponse } = await openIngridSession(ctx, opts)

      if (error || !ingridResponse) {
        throw new Error(error || 'Unknown error')
      }

      return {
        provider: 'INGRID',
        shipping: {
          provider: 'INGRID',
          providerSessionId: ingridResponse.session.checkout_session_id
        },
        response: { ingridResponse }
      }
    }

    case 'NSHIFT': {
      const nshiftResponse = await openNshiftSession(ctx, opts)

      return {
        provider: 'NSHIFT',
        shipping: { provider: 'NSHIFT', providerSessionId: nshiftResponse.sessionId },
        response: { nshiftResponse }
      }
    }
  }
}
